import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle, Edit, Plus, Server, Trash2, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'

import Button from '../components/ui/Button'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import Input from '../components/ui/Input'
import Modal, { ConfirmationModal } from '../components/ui/Modal'
import { StatusBadge } from '../components/ui/Badge'

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const readApiError = async (response: Response, fallback: string) => {
  try {
    const data = await response.json()
    return typeof data.detail === 'string' ? data.detail : data.detail?.message || fallback
  } catch {
    return fallback
  }
}

const api = {
  list: async () => {
    const response = await fetch('/api/v1/v2board-panels/', { headers: authHeaders() })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to load panels'))
    return response.json()
  },
  save: async ({ id, data }: { id?: number; data: any }) => {
    const response = await fetch(id ? `/api/v1/v2board-panels/${id}` : '/api/v1/v2board-panels/', {
      method: id ? 'PUT' : 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to save panel'))
    return response.json()
  },
  test: async (id: number) => {
    const response = await fetch(`/api/v1/v2board-panels/${id}/test`, {
      method: 'POST',
      headers: authHeaders(),
    })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to test panel'))
    return response.json()
  },
  remove: async (id: number) => {
    const response = await fetch(`/api/v1/v2board-panels/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    if (!response.ok) throw new Error(await readApiError(response, 'Failed to delete panel'))
  },
}

const emptyForm = {
  name: '',
  admin_url: '',
  api_url: '',
  secure_path: '',
  auth_scheme: 'raw',
  auth_data: '',
  admin_email: '',
  admin_password: '',
  enabled: true,
}

const V2BoardPanels = () => {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<any>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<any>(null)
  const [form, setForm] = useState<any>(emptyForm)

  const { data: panels = [], isLoading } = useQuery({ queryKey: ['v2board-panels'], queryFn: api.list })

  const saveMutation = useMutation({
    mutationFn: api.save,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['v2board-panels'] })
      toast.success('V2Board panel saved')
      setFormOpen(false)
    },
  })

  const testMutation = useMutation({
    mutationFn: api.test,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['v2board-panels'] })
      toast.success(`Connection OK. Read ${data.node_count || 0} nodes.`)
    },
    onError: (error: any) => toast.error(error.message || 'Connection failed'),
  })

  const deleteMutation = useMutation({
    mutationFn: api.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['v2board-panels'] })
      toast.success('V2Board panel deleted')
      setDeleteTarget(null)
    },
    onError: (error: any) => toast.error(error.message || 'Delete failed'),
  })

  const openForm = (panel?: any) => {
    setEditing(panel || null)
    setForm({
      ...emptyForm,
      ...panel,
      auth_data: '',
      admin_password: '',
    })
    setFormOpen(true)
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const payload = Object.fromEntries(Object.entries(form).filter(([, value]) => value !== ''))
    await saveMutation.mutateAsync({ id: editing?.id, data: payload })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">V2Board Panels</h1>
          <p className="text-secondary-500 dark:text-secondary-400 mt-1">
            Add each V2Board once, then choose panel and group ID in Providers.
          </p>
        </div>
        <Button variant="primary" onClick={() => openForm()} leftIcon={<Plus className="w-4 h-4" />}>
          Add Panel
        </Button>
      </div>

      <Card>
        <CardHeader title="Panels" description={`${panels.length} configured panel(s)`} />
        <CardContent>
          {isLoading ? (
            <div className="py-10 text-center text-secondary-500">Loading...</div>
          ) : panels.length === 0 ? (
            <div className="py-12 text-center">
              <Server className="w-10 h-10 mx-auto mb-3 text-secondary-400" />
              <div className="font-medium text-secondary-900 dark:text-white">No V2Board panels yet</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200 dark:border-secondary-700">
                    <th className="text-left py-3 px-4 text-sm font-medium">Panel</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">API URL</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Auth</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                  {panels.map((panel: any) => (
                    <tr key={panel.id}>
                      <td className="py-3 px-4 font-medium text-secondary-900 dark:text-white">{panel.name}</td>
                      <td className="py-3 px-4 text-sm text-secondary-600 dark:text-secondary-400">{panel.api_url}</td>
                      <td className="py-3 px-4">
                        {panel.has_auth_data ? <CheckCircle className="w-5 h-5 text-success-500" /> : <XCircle className="w-5 h-5 text-error-500" />}
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={panel.last_test_status || 'pending'} size="sm" />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => testMutation.mutate(panel.id)}>Test</Button>
                          <Button variant="ghost" size="sm" onClick={() => openForm(panel)} leftIcon={<Edit className="w-3 h-3" />}>Edit</Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(panel)} leftIcon={<Trash2 className="w-3 h-3" />}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title={editing ? 'Edit V2Board Panel' : 'Add V2Board Panel'} size="lg" hideFooter>
        <form onSubmit={submit} className="space-y-4">
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <Input label="Admin URL" value={form.admin_url || ''} onChange={(e) => setForm({ ...form, admin_url: e.target.value })} placeholder="https://domain.com/securepath#/login" helperText="API URL and secure path can be inferred from this." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="API URL" value={form.api_url || ''} onChange={(e) => setForm({ ...form, api_url: e.target.value })} placeholder="https://domain.com" />
            <Input label="Secure Path" value={form.secure_path || ''} onChange={(e) => setForm({ ...form, secure_path: e.target.value })} placeholder="146db25b" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Admin Email" value={form.admin_email || ''} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
            <Input label="Admin Password" type="password" value={form.admin_password || ''} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} placeholder={editing ? 'Leave blank to keep existing' : ''} />
          </div>
          <Input label="Auth Token" value={form.auth_data || ''} onChange={(e) => setForm({ ...form, auth_data: e.target.value })} placeholder={editing ? 'Leave blank to keep existing' : 'Optional if admin login is provided'} />
          <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" loading={saveMutation.isPending}>Save Panel</Button>
          </div>
        </form>
      </Modal>

      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutateAsync(deleteTarget.id)}
        message={`Delete panel "${deleteTarget?.name}"? Providers using this panel must be changed first.`}
        confirmText="Delete Panel"
        confirmVariant="danger"
        destructive
      />
    </div>
  )
}

export default V2BoardPanels
